import { useMutation, useQuery, useQueryClient } from "react-query";
import { fetchApi } from "../../../helpers/reactQueryApi";
import { stringify } from "query-string";
import { isNull, omitBy } from "lodash";

type PropertySetParams = {
  name: "file_profile" | "folder_profile" | string;
  contextType: "network" | "group" | string;
  contextId?: string;
  inherit?: boolean;
};

export const propertySetUrl = ({
  name,
  contextType,
  contextId,
  inherit,
}: PropertySetParams) =>
  `/api/v1/property_sets/${name}?${stringify(
    omitBy(
      {
        context_type: contextType,
        context_id: contextId,
        inherit,
      },
      isNull,
    ),
  )}`;

export type PropertySetProperty = {
  type: string;
  name: string;
  label: string;
  required?: boolean;
  read_only?: boolean;
  // e-mail fields of a contact type: the one the mailing goes by (#652)
  use_for_mailing?: boolean;
  [key: string]: any;
};

type PropertySetPayload = {
  type: "modified" | "basic";
  properties: PropertySetProperty[];
  tag_parents?: string[];
};

export function usePropertySet(params: PropertySetParams, options?) {
  options = options || {};

  // Disable requests for file_ or folder_profile property_sets if they are disabled via network config
  if (
    (options.enabled === undefined && params.name === "file_profile") ||
    params.name == "folder_profile"
  ) {
    const profileConfig = Preload.current_network.config[`${params.name}s`];
    if (!profileConfig?.enabled) options.enabled = false;
  }

  return useQuery<PropertySetPayload>(propertySetUrl(params), {
    staleTime: Infinity,
    retry: false, // disable retry to enable fast rendering of folder listing in case something is misconfigured
    ...options,
  });
}

function persistPropertySet({
  body,
  ...params
}: PropertySetParams & { body: any }) {
  return fetchApi(propertySetUrl(params), {
    method: "PUT",
    body,
  });
}

export function usePersistPropertySet(options?) {
  const queryClient = useQueryClient();
  return useMutation(persistPropertySet, {
    ...options,
    // usePropertySet cacht mit staleTime: Infinity — ohne Invalidierung zeigt
    // der Editor nach SPA-Navigation weg und zurück den Stand vor dem Save.
    onSuccess: (data, variables, context) => {
      // Per Prädikat über den URL-Prefix: die Query-Keys tragen zusätzliche
      // Parameter (z.B. inherit=false im Editor, inherit=true im Datei-Modul),
      // die die Mutation nicht kennt — ein exakter Key-Match träfe nichts.
      const prefix = `/api/v1/property_sets/${variables.name}?`;
      queryClient.invalidateQueries({
        // NOT a bare queryKey[0]: react-query v3 stores the key exactly as
        // passed, and usePropertySet keys are plain URL STRINGS — [0] would
        // be the first character and the predicate would never match (the
        // editor then shows pre-save data after a remount, e.g. tab
        // switches). Array.isArray guard as in appointments/tasks api.
        predicate: (query) => {
          const key = Array.isArray(query.queryKey)
            ? query.queryKey[0]
            : query.queryKey;
          return String(key ?? "").startsWith(prefix);
        },
      });
      // the fields of a contact type are the candidates for its display name (tixxt/core#657)
      if (variables.contextType === "contact_type" && variables.contextId) {
        queryClient.invalidateQueries(["contactTypes", variables.contextId]);
      }

      options?.onSuccess?.(data, variables, context);
    },
  });
}
